// Helpers d'affichage d'un événement, partagés entre le calendrier (liste) et la page
// dédiée d'un événement (/evenement/:id) -- avant, tout vivait dans Calendar.vue avec
// la modale de détails, que la page dédiée remplace pour offrir un lien partageable.
import backendApi from '@/axios/backendApi';
import { backend_url } from '@/config/config';

export type JurisdictionNames = Record<string, string>;

// Code -> nom complet des comités/ligues, pour les filtres du calendrier -- public,
// national (Calendrier n'est pas propre à un seul comité/une seule ligue, contrairement à CMER).
// `comiteLigues` (code comité -> code ligue) sert aux filtres du calendrier, pour ne
// proposer que les comités de la ligue choisie.
export const loadJurisdictionNames = async (): Promise<{
  comites: JurisdictionNames; ligues: JurisdictionNames; comiteLigues: JurisdictionNames;
}> => {
  try {
    const [{ data: comites }, { data: ligues }] = await Promise.all([
      backendApi.get('/clubs/comites'),
      backendApi.get('/clubs/ligues'),
    ]);
    return {
      comites: Object.fromEntries(comites.map((c: { code: string; name: string }) => [c.code, c.name])),
      ligues: Object.fromEntries(ligues.map((l: { code: string; name: string }) => [l.code, l.name])),
      comiteLigues: Object.fromEntries(comites.map((c: { code: string; ligue_code: string | null }) => [c.code, c.ligue_code ?? ''])),
    };
  } catch {
    // silencieux -- l'événement s'affiche alors avec son nom brut, sans qualificatif.
    return { comites: {}, ligues: {}, comiteLigues: {} };
  }
};

// Nom numéroté ("Open de Comité #2") quand le backend l'a calculé, nom brut sinon --
// pour tout AFFICHAGE du nom d'un événement ; la logique (types, filtres) garde `name`.
export const eventTitle = (event: Pick<OfficialEvent, 'name' | 'display_name'> | null | undefined): string =>
  event?.display_name || event?.name || '';

// URLs lisibles "/evenement/46-open-de-comite-2-montpellier" : l'id en tête reste la seule
// clé (parseEventId), le reste n'est que décoratif -- un renommage ne casse donc aucun lien,
// et les anciens liens "/evenement/46" continuent de marcher (EventDetails redirige vers
// l'URL complète). Même algorithme que Dartshub events/routes.py::_event_slug (sitemap).
const slugify = (str: string): string =>
  str
    .toLowerCase()
    .replace(/œ/g, 'oe')
    .replace(/æ/g, 'ae')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

export const eventSlug = (event: Pick<OfficialEvent, 'id' | 'name' | 'display_name' | 'place'>): string => {
  const words = slugify([eventTitle(event), event.place].filter(Boolean).join(' '))
    .slice(0, 80)
    .replace(/-+$/, '');
  return words ? `${event.id}-${words}` : String(event.id);
};

export const eventPath = (event: Pick<OfficialEvent, 'id' | 'name' | 'display_name' | 'place'>): string =>
  `/evenement/${eventSlug(event)}`;

export const registrationPath = (event: Pick<OfficialEvent, 'id' | 'name' | 'display_name' | 'place'>): string =>
  `/inscription/${eventSlug(event)}`;

export const parseEventId = (param: unknown): number => parseInt(String(param), 10);

// Nom complet ("Coupe du Comité Réunion", "Open de Comité #2 (MER)"), calculé par le
// backend (Dartshub events/numbering.py::full_event_name) pour être identique dans tous
// les outils -- repli sur le nom numéroté si absent.
export const displayEventName = (event: Pick<OfficialEvent, 'name' | 'display_name' | 'full_name'>): string =>
  event.full_name || eventTitle(event);

// price_cents vaut 0 par défaut en base dès qu'un tarif n'a jamais été saisi (colonne
// non nullable, cf. EventCreate.price_cents = 0) -- indiscernable d'un tarif
// explicitement gratuit. La plupart des événements du calendrier officiel n'ont jamais
// de tarif saisi (LSEF ne gère pas l'inscription en ligne), donc on n'affiche rien
// plutôt que d'afficher "Gratuit" à tort sur des événements qui ne prennent pas
// d'inscription via cet outil.
// Une entrée par catégorie (individuel / double) : le calendrier et la fiche tournoi les
// empilent sur mobile, une seule ligne débordant de l'écran.
export const formatPriceParts = (event: OfficialEvent): string[] => {
  const licensed = event.price_cents ?? 0;
  // Une coupe n'accepte jamais de non licencié (règlement) : son tarif non licencié ne
  // concerne personne. Le statut "Licenciés uniquement" n'est en revanche souvent qu'une
  // première phase avant l'ouverture aux non licenciés : on affiche alors les deux tarifs.
  const unlicensed = event.cup_scope ? licensed : (event.price_cents_unlicensed ?? licensed);
  const team = teamPrices(event);
  if (licensed === 0 && unlicensed === 0 && !team) return [];
  const perPlayer = unlicensed === licensed
    ? `${(licensed / 100).toFixed(2)} € / catégorie`
    : `${(licensed / 100).toFixed(2)} € licencié / ${(unlicensed / 100).toFixed(2)} € non licencié`;
  if (!team) return [perPlayer];
  const perTeam = team.unlicensed === team.licensed
    ? `${(team.licensed / 100).toFixed(2)} €`
    : `${(team.licensed / 100).toFixed(2)} € licenciés / ${(team.unlicensed / 100).toFixed(2)} € non licenciés`;
  return [`Individuel : ${perPlayer}`, `Double : ${perTeam} / équipe`];
};

export const formatPrice = (event: OfficialEvent): string | null =>
  formatPriceParts(event).join(' · ') || null;

// Prix par équipe (licencié / non licencié) quand le mode est actif, sinon null -- cf.
// Dartshub events/models.py::Event.team_pricing. Un tournoi local ou une coupe n'a qu'un
// seul prix d'équipe utile (cf. formatPrice pour le statut "Licenciés uniquement").
export const teamPrices = (event: any): { licensed: number; unlicensed: number } | null => {
  if (!event?.team_pricing || event.price_team_cents == null) return null;
  const licensed = event.price_team_cents;
  const singleRate = event.event_type === 'local' || !!event.cup_scope;
  const unlicensed = singleRate ? licensed : (event.price_team_cents_unlicensed ?? licensed);
  return { licensed, unlicensed };
};

// Part d'UN joueur pour UNE catégorie double, en centimes : le tarif par joueur hors mode
// équipe, sinon la moitié du prix d'équipe de SON statut (une équipe mixte paie donc la
// moyenne des deux) -- miroir de Dartshub events/routes.py::_double_share_cents_for.
export const doubleShareCents = (event: any, isLicensed: boolean | null | undefined, perPlayerCents: number): number => {
  const team = teamPrices(event);
  if (!team) return perPlayerCents;
  return Math.floor((isLicensed || event.event_type === 'local' ? team.licensed : team.unlicensed) / 2);
};

// Un club saisit parfois une adresse mail dans "Lien externe" plutôt qu'une vraie URL --
// utilisée telle quelle comme href, le navigateur la traite comme un chemin relatif et
// atterrit sur une 404 (bug signalé le 2026-09-21). On reconnaît l'email (pas de schéma,
// contient "@") pour construire un lien "mailto:", et on ajoute "https://" devant un nom
// de domaine saisi sans protocole (ex. "monclub.fr").
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const resolveExternalLink = (value: string | null | undefined): string | null => {
  const v = (value || '').trim();
  if (!v) return null;
  if (EMAIL_RE.test(v)) return `mailto:${v}`;
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(v)) return v; // déjà un schéma (http:, https:, mailto:...)
  return `https://${v}`;
};

// Un event lié à un tournoi Badarts (cf. linked_badarts_tournament_id) reçoit une URL
// ABSOLUE (https://api.badarts.fr/uploads/...), l'affiche vivant sur l'autre service --
// jamais préfixée par backend_url (celui de DartsHub), sinon l'image casse (404).
export const getEventImage = (event: OfficialEvent): string | null =>
  event.affiche ? (/^https?:\/\//.test(event.affiche) ? event.affiche : `${backend_url}${event.affiche}`) : null;

// `registration_open` est calculé par le backend : au moins un mode de paiement est
// réellement disponible (en ligne avec de vrais identifiants HelloAsso chez l'entité
// gestionnaire, ou sur place avec un accès premium actif). Sans ça, "S'inscrire" menait
// à un formulaire qui échouait systématiquement à l'étape paiement.
export const canRegister = (event: OfficialEvent): boolean =>
  event.registration_status !== 'closed' && !!event.registration_open && !deadlinePassed(event);

// Libellé des modes de paiement proposés au joueur, null si aucun (inscriptions pas
// encore ouvrables) -- affiché sur la fiche et le formulaire d'inscription.
export const paymentModesLabel = (
  event: Pick<OfficialEvent, 'online_payment_available' | 'onsite_payment_available'>,
): string | null => {
  if (event.online_payment_available && event.onsite_payment_available) return 'Paiement en ligne ou sur place';
  if (event.online_payment_available) return 'Paiement en ligne';
  if (event.onsite_payment_available) return 'Paiement sur place';
  return null;
};

// Butoir de clôture automatique dépassée : le flag backend fait foi au chargement, la
// comparaison locale prend le relais si la page reste ouverte au-delà de l'heure butoir
// (le serveur refuse de toute façon l'inscription, cf. events/routes.py::register_and_pay).
export const deadlinePassed = (event: Pick<OfficialEvent, 'registration_deadline' | 'registration_deadline_passed'>): boolean => {
  if (event.registration_deadline_passed) return true;
  if (!event.registration_deadline) return false;
  const deadline = new Date(event.registration_deadline);
  return !isNaN(deadline.getTime()) && deadline.getTime() <= Date.now();
};

// "dimanche 12 octobre 2025 à 18:00", dans le fuseau du navigateur.
export const formatDeadline = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

// Même mise en forme que useEventStore.fetchEvents : la réponse brute de l'API
// (event_date/location/club_name) vers la forme OfficialEvent (date/place/organiser)
// utilisée partout côté front.
export const toOfficialEvent = (data: any): OfficialEvent => ({
  ...data,
  date: data.event_date,
  date_end: data.event_date_end ?? null,
  place: data.location,
  organiser: data.club_name,
  organizer_club_id: data.organizer_club_id ?? null,
  affiche: data.affiche ?? null,
  format: data.format ?? null,
  price_cents: data.price_cents ?? 0,
  created_by_email: data.created_by_email ?? null,
});
